/*
HuntPanel — mirror Android v1.2.0 `HuntPanel.kt` + v1.3.1 easter egg
obscure + v1.5.0 blip directionnel. Panneau bas plein-largeur qui guide
vers le remarquable non capturé le plus proche, avec un radar animé
(rotation 5 s, ping + flash à chaque refresh) et une distance live.

v1.5.0 : le radar affiche un blip north-up (0° = nord géographique,
aucun capteur de cap — cohérent avec la carte) dont le rayon encode la
distance en échelle log (`distanceToRadiusFrac`). Révélation sonar : la
nouvelle lecture (distance + position du blip) n'est affichée qu'au
passage de la barre sur le blip courant — chiffre et blip sautent
ensemble, avec le ping. Sous 25 m le blip disparaît (bruit GPS, le
bearing n'a plus de sens) au profit d'un point central qui bat ;
hystérésis de sortie à 35 m.

Easter egg v1.3.1 : triple-tap sur le wrapper statique du radar (3
taps en < 600 ms cumulés) → toggle un mode obscure persistant qui
remplace titre + qualification de la cible par « ??? ». Distance live
conservée intacte. Mode caché, non communiqué dans l'UI. Bidirectionnel.
Persistance via `radarObscureStore` (IDB meta, équivalent
DataStore Preferences Android `RadarObscureStore.kt`).

Le radar tourne TOUJOURS, y compris en `prefers-reduced-motion` —
parité Android FrameClock.kt : le device cible (GrapheneOS, échelle
d'animation système 0, que Vanadium traduit en reduced-motion) doit
voir le balayage, c'est le retour visuel du scan. Android a construit
`withFrameNanos` précisément pour ça (HuntPanel.kt:130-163).

API : `mountHuntPanel(host, opts) → { destroy, refresh }`. Le panneau
est un détacheable autonome : `destroy()` libère le rAF + listeners +
DOM. Le state `obscured` initial est passé par l'appelant (chargement
async hors panneau) ; le toggle ré-écrit via le callback.
*/

import { haversineMeters, initialBearingDegrees, type FreshFix } from '../../capture/geolocation'
import type { ArbreFeature } from '../../data/arbresFeatures'

const kCycleMs: number = 5000  // sweep + recompute cadence
const kPulseDurationMs: number = 260  // ping + flash décay
const kCometArcDeg: number = 70  // pie-slice trainant derrière le sweep
const kRadarSizePx: number = 76  // logical size; canvas backing scaled by DPR
const kBaseLineWidth: number = 1.6  // pre-pulse stroke width (1.6 dp Android)
const kNearThresholdM: number = 10  // « tout près ! » — NEAR_THRESHOLD_M Android
const kDirectionHideM: number = 25  // entrée nearMode : blip masqué (bruit GPS) — DIRECTION_HIDE_M
const kDirectionShowM: number = 35  // sortie nearMode (hystérésis) — DIRECTION_SHOW_M
const kRadarEdgeM: number = 2000  // distance projetée sur l'anneau extérieur — RADAR_EDGE_M
const kBlipRadiusPx: number = 3.5  // rayon du blip (3.5 dp Android)

/*
Rayon du blip sur le cadran (fraction 0..1 du rayon) en fonction de la
distance — mapping log, mirror exact Android `distanceToRadiusFrac`
(HuntPanel.kt) : frac = clamp(ln(d/25) / ln(2000/25), 0, 1).
Valeurs du DistanceToRadiusFracTest Android : 25→0, 2000→1, capes
5→0 / 0→0 / 10000→1, anneaux 106→0.33±0.01, 452→0.66±0.01,
strictement croissante sur [25..2000].
*/
function distanceToRadiusFrac(inDistanceM: number): number
{
	const vRaw = Math.log(inDistanceM / kDirectionHideM) / Math.log(kRadarEdgeM / kDirectionHideM)
	return Math.max(0, Math.min(1, vRaw))
}

/*
La barre a-t-elle franchi l'angle cible entre deux frames ? Intervalle
ouvert-fermé (prev, cur], wrap 0/360 géré — mirror Android `sweepCrossed`.
*/
function sweepCrossed(inPrevDeg: number, inCurDeg: number, inTargetDeg: number): boolean
{
	const vNorm = (inDeg: number): number => ((inDeg % 360) + 360) % 360
	const vPrev = vNorm(inPrevDeg)
	const vCur = vNorm(inCurDeg)
	const vTarget = vNorm(inTargetDeg)
	return vPrev <= vCur ? (vTarget > vPrev && vTarget <= vCur) : (vTarget > vPrev || vTarget <= vCur)
}

/*
Résolution discriminée du readout — chaque état pilote un rendu HTML
distinct dans `renderTarget`.
*/
type HuntReadout =
	| { kind: 'loading' }
	| { kind: 'no-fix'; sample: ArbreFeature }      // pas de fix — montre un placeholder cible
	| { kind: 'all-discovered' }                    // 183 remarquables capturés
	| { kind: 'locked'; target: ArbreFeature; distanceM: number; bearingDeg: number }  // bearing géographique user→cible, 0 = nord

export interface HuntPanelOpts
{
	remarquables: ArbreFeature[]                      // arbres remarquables (filtrés par MapScreen)
	getCapturedIds: () => ReadonlySet<number>         // accessor live aux captures (synchro avec MapScreen state)
	getFix: () => FreshFix | null                     // accessor live au fix GPS courant
	resolveName: (inArbre: ArbreFeature) => string    // displayName(speciesNameFor(sk)) | « Espèce indéterminée »
	resolveQualification: (inArbre: ArbreFeature) => string | null  // remarquableInfoFor(id)?.qualif ?? null
	onClose: () => void                               // tap ✕ ou unmount
	initialObscured: boolean                          // v1.3.1 easter egg — state chargé async par l'appelant
	onObscureToggle: (inNext: boolean) => void        // v1.3.1 easter egg — persistance déléguée à l'appelant (fire-and-forget)
}

export interface HuntPanelController
{
	destroy: () => void
	refresh: () => void  // appel externe après une capture pour recalc immédiat
}

export function mountHuntPanel(inHost: HTMLElement, inOpts: HuntPanelOpts): HuntPanelController
{
	inHost.innerHTML = renderShell()
	const vRoot = inHost.querySelector<HTMLElement>('.hunt-panel')!
	const vTargetSlot = vRoot.querySelector<HTMLElement>('.hunt-panel-target')!
	const vDistanceSlot = vRoot.querySelector<HTMLElement>('.hunt-panel-distance')!
	const vRadarWrap = vRoot.querySelector<HTMLElement>('.hunt-radar')!
	const vCanvas = vRoot.querySelector<HTMLCanvasElement>('.hunt-radar-canvas')!
	const vCloseBtn = vRoot.querySelector<HTMLButtonElement>('.hunt-panel-close')!

	// Setup canvas (DPR-aware).
	const vDpr = Math.max(1, window.devicePixelRatio || 1)
	vCanvas.width = kRadarSizePx * vDpr
	vCanvas.height = kRadarSizePx * vDpr
	vCanvas.style.width = `${kRadarSizePx}px`
	vCanvas.style.height = `${kRadarSizePx}px`
	const vCtx = vCanvas.getContext('2d')
	if (vCtx) vCtx.scale(vDpr, vDpr)

	// State machine. v1.5.0 : recompute (au wrap du cycle) et commit
	// (affichage) sont découplés — cf. « révélation sonar » du header.
	let fStart: number = performance.now()
	let fLastCycle: number = -1  // forces a recompute on first frame
	let fCommittedReadout: HuntReadout = { kind: 'loading' }  // la lecture AFFICHÉE (texte + distance + blip)
	let fPendingReadout: HuntReadout | null = null  // recomputée au wrap, en attente du passage de barre
	let fNearMode: boolean = false  // latch hystérésis : entre < 25 m, sort > 35 m
	let fPrevSweepDeg: number = 0  // angle de barre de la frame précédente (pour sweepCrossed)
	let fPulseStart: number = -Infinity  // <0 = no pulse active
	let fDisposed = false
	let fObscured: boolean = inOpts.initialObscured  // v1.3.1 easter egg

	// Triple-tap detector (window 600 ms cumulés sur 3 taps). Listener
	// placé sur le wrapper statique `.hunt-radar`, PAS sur le canvas
	// dont la rotation du sweep fausserait le hit-test (parité Android
	// HuntPanel.kt:300-316).
	const kTripleTapWindowMs = 600
	const vTapTimes: number[] = []
	function onRadarTap(): void
	{
		const vNow = performance.now()
		vTapTimes.push(vNow)
		while (vTapTimes.length > 3) vTapTimes.shift()
		if (vTapTimes.length === 3 && (vTapTimes[2]! - vTapTimes[0]!) <= kTripleTapWindowMs)
		{
			fObscured = !fObscured
			vTapTimes.length = 0
			inOpts.onObscureToggle(fObscured)
			renderTarget()
		}
	}

	function recomputeReadout(): HuntReadout
	{
		const vCaptured = inOpts.getCapturedIds()
		const vCandidates = inOpts.remarquables.filter((inR) => !vCaptured.has(inR.id))
		if (vCandidates.length === 0) return { kind: 'all-discovered' }
		const vFix = inOpts.getFix()
		if (!vFix) return { kind: 'no-fix', sample: vCandidates[0]! }
		let vBest = vCandidates[0]!
		let vBestDist = haversineMeters(vFix.lat, vFix.lon, vBest.lat, vBest.lon)
		for (let vI = 1; vI < vCandidates.length; vI++)
		{
			const vR = vCandidates[vI]!
			const vD = haversineMeters(vFix.lat, vFix.lon, vR.lat, vR.lon)
			if (vD < vBestDist) { vBest = vR; vBestDist = vD }
		}
		return {
			kind: 'locked',
			target: vBest,
			distanceM: vBestDist,
			bearingDeg: initialBearingDegrees(vFix.lat, vFix.lon, vBest.lat, vBest.lon),
		}
	}

	// Latch nearMode avec hystérésis (parité Android updateNearMode) :
	// entre < 25 m, sort > 35 m ; un readout non-locked (no-fix, loading,
	// all-discovered) RÉINITIALISE le latch (else → false Android). Appelé
	// exclusivement par commitReadout, comme le commit() Android — le gate
	// « un blip est-il affiché ? » au wrap doit voir l'ANCIEN nearMode.
	function updateNearMode(inReadout: HuntReadout): void
	{
		if (inReadout.kind !== 'locked') { fNearMode = false; return }
		if (inReadout.distanceM < kDirectionHideM) fNearMode = true
		else if (inReadout.distanceM > kDirectionShowM) fNearMode = false
	}

	// Le blip actuellement à l'écran, dérivé de la lecture COMMITTÉE.
	// null si nearMode (pas de direction fiable) ou pas de cible lockée.
	function committedBlip(): { bearingDeg: number; frac: number } | null
	{
		if (fNearMode || fCommittedReadout.kind !== 'locked') return null
		return {
			bearingDeg: fCommittedReadout.bearingDeg,
			frac: distanceToRadiusFrac(fCommittedReadout.distanceM),
		}
	}

	// Commit théâtral : texte + distance + blip + nearMode + ping sautent
	// ensemble (le latch n'évolue qu'ici, parité Android commit()).
	function commitReadout(inReadout: HuntReadout, inNow: number): void
	{
		fCommittedReadout = inReadout
		fPendingReadout = null
		updateNearMode(inReadout)
		renderTarget()
		fPulseStart = inNow
	}

	function renderTarget(): void
	{
		vTargetSlot.innerHTML = renderTargetHtml(fCommittedReadout, inOpts, fObscured)
		vDistanceSlot.innerHTML = renderDistanceHtml(fCommittedReadout)
	}

	function drawFrame(inNow: number): void
	{
		if (fDisposed) return
		const vCtx = vCanvas.getContext('2d')
		if (!vCtx) return
		const vElapsed = inNow - fStart
		const vCycle = Math.floor(vElapsed / kCycleMs)
		const vSweepDeg = ((vElapsed % kCycleMs) / kCycleMs) * 360
		if (vCycle !== fLastCycle)
		{
			fLastCycle = vCycle
			const vNew = recomputeReadout()
			// Révélation sonar : si un blip est à l'écran (état COMMITTÉ,
			// ancien nearMode — parité Android), la nouvelle lecture reste
			// en attente jusqu'au passage de la barre sur le blip AFFICHÉ.
			// Sinon (loading / no-fix / all-discovered / nearMode) : commit
			// direct, ping à midi.
			if (committedBlip()) fPendingReadout = vNew
			else commitReadout(vNew, inNow)
		}
		if (fPendingReadout)
		{
			const vBlip = committedBlip()
			// Garde !vBlip : si le blip affiché a disparu entre-temps
			// (nearMode flippé), ne pas laisser la pending en rade.
			if (!vBlip || sweepCrossed(fPrevSweepDeg, vSweepDeg, vBlip.bearingDeg)) commitReadout(fPendingReadout, inNow)
		}
		fPrevSweepDeg = vSweepDeg
		const vPulse = Math.max(0, 1 - (inNow - fPulseStart) / kPulseDurationMs)
		drawRadar(vCtx, vSweepDeg, vPulse, committedBlip(), fNearMode)
		fRafHandle = window.requestAnimationFrame(drawFrame)
	}

	let fRafHandle: number = window.requestAnimationFrame(drawFrame)

	vCloseBtn.addEventListener('click', inOpts.onClose)
	vRadarWrap.addEventListener('click', onRadarTap)

	return {
		destroy: (): void => {
			fDisposed = true
			if (fRafHandle) window.cancelAnimationFrame(fRafHandle)
			vCloseBtn.removeEventListener('click', inOpts.onClose)
			vRadarWrap.removeEventListener('click', onRadarTap)
			inHost.innerHTML = ''
		},
		refresh: (): void => {
			// Forcer un recompute + commit immédiat (utilisé par MapScreen
			// après une capture validée alors que la chasse est active :
			// la cible peut changer immédiatement — pas d'attente
			// théâtrale du passage de barre).
			commitReadout(recomputeReadout(), performance.now())
		},
	}
}

function renderShell(): string
{
	return `
		<div class="hunt-panel" role="dialog" aria-label="Mode chasse Étoile">
			<div class="hunt-panel-header">Arbre remarquable non capturé le plus proche</div>
			<div class="hunt-panel-row">
				<div class="hunt-radar" aria-hidden="true">
					<div class="hunt-radar-north">N</div>
					<canvas class="hunt-radar-canvas"></canvas>
				</div>
				<div class="hunt-panel-target"></div>
				<div class="hunt-panel-distance"></div>
			</div>
			<button class="hunt-panel-close" type="button" aria-label="Fermer le mode chasse">✕</button>
		</div>
	`
}

function renderTargetHtml(inReadout: HuntReadout, inOpts: HuntPanelOpts, inObscured: boolean): string
{
	// v1.3.1 easter egg : en mode obscure, les cas qui révèlent une
	// cible (no-fix, locked) masquent titre + qualification par « ??? ».
	// Les cas loading / all-discovered ne sont pas affectés (rien à
	// obscurer). Distance live conservée intacte côté renderDistanceHtml.
	switch (inReadout.kind)
	{
	case 'loading':
		return `<div class="hunt-target-title">Chargement…</div>`
	case 'all-discovered':
		return `
			<div class="hunt-target-title">Tous les remarquables découverts</div>
			<div class="hunt-target-subtitle">Bravo 🎉</div>
		`
	case 'no-fix':
	{
		if (inObscured)
		{
			return `
				<div class="hunt-target-title">???</div>
				<div class="hunt-target-subtitle">??? · localisation en cours…</div>
			`
		}
		const vName = inOpts.resolveName(inReadout.sample)
		const vQualif = glossQualification(inOpts.resolveQualification(inReadout.sample))
		return `
			<div class="hunt-target-title">${escapeHtml(vName)}</div>
			<div class="hunt-target-subtitle">${escapeHtml(vQualif)} · localisation en cours…</div>
		`
	}
	case 'locked':
	{
		if (inObscured)
		{
			return `
				<div class="hunt-target-title">???</div>
				<div class="hunt-target-subtitle">???</div>
			`
		}
		const vName = inOpts.resolveName(inReadout.target)
		const vQualif = glossQualification(inOpts.resolveQualification(inReadout.target))
		return `
			<div class="hunt-target-title">${escapeHtml(vName)}</div>
			<div class="hunt-target-subtitle">${escapeHtml(vQualif)}</div>
		`
	}
	}
}

function renderDistanceHtml(inReadout: HuntReadout): string
{
	if (inReadout.kind !== 'locked') return ''
	const vD = inReadout.distanceM
	if (vD < kNearThresholdM) return `<span class="hunt-distance-near">tout près !</span>`
	const vRounded = vD > 50 ? Math.round(vD / 5) * 5 : Math.round(vD)
	return `<span class="hunt-distance-text">${vRounded} m</span>`
}

/*
Glose la qualification d'un remarquable. Mirror Android
HuntPanel.kt:glossQualification.
- Vide / null      → « Arbre remarquable »
- Contient espace  → tel quel (« Arbre vénérable »…)
- Mot unique       → « Intérêt {lowercase} » (« Intérêt historique »…)
*/
function glossQualification(inQ: string | null): string
{
	const vTrim = inQ?.trim() ?? ''
	if (vTrim.length === 0) return 'Arbre remarquable'
	if (vTrim.includes(' ')) return vTrim
	return `Intérêt ${vTrim.toLowerCase()}`
}

/*
Dessine le radar — 3 cercles concentriques (fracs 0.33/0.66/1 alignés
sur le mapping log du blip), blip north-up révélé par la barre, sweep
rotation, pie-slice comète trainant, ping + flash décalés sur le pulse.
Rendu aligné trait pour trait sur Android `RadarGlyph` (HuntPanel.kt).
Le blip est peint AVANT la couche rotative : c'est la barre qui passe
au-dessus de lui (parité deux-couches Android).
*/
function drawRadar(
	inCtx: CanvasRenderingContext2D,
	inSweepDeg: number,
	inPulse: number,
	inBlip: { bearingDeg: number; frac: number } | null,
	inNearMode: boolean,
): void
{
	const vSize = kRadarSizePx
	const vCenter = vSize / 2
	const vRadius = vSize / 2 - 2

	inCtx.clearRect(0, 0, vSize, vSize)

	const vColor = 'rgb(217, 122, 58)'  // canopaname-remarquable-orange, fallback hardcoded pour les écrans sans CSS resolution

	// 3 cercles concentriques.
	for (const [vMul, vAlpha] of [[1.0, 0.45], [0.66, 0.30], [0.33, 0.20]] as const)
	{
		inCtx.beginPath()
		inCtx.arc(vCenter, vCenter, vRadius * vMul, 0, Math.PI * 2)
		inCtx.strokeStyle = `rgba(217, 122, 58, ${vAlpha})`
		inCtx.lineWidth = 1.2
		inCtx.stroke()
	}

	// Ping qui jaillit du centre au cycle (alpha décroît avec pulse).
	if (inPulse > 0.01)
	{
		inCtx.beginPath()
		inCtx.arc(vCenter, vCenter, vRadius * (1 - inPulse), 0, Math.PI * 2)
		inCtx.strokeStyle = `rgba(217, 122, 58, ${(inPulse * 0.5).toFixed(3)})`
		inCtx.lineWidth = 1.4
		inCtx.stroke()
	}

	// Blip north-up : X = +sin, Y = −cos (0° = nord = haut). Alpha plein
	// juste après le passage de la barre, décroît vers un plancher 0.3
	// jusqu'au tour suivant — c'est la barre qui le révèle.
	if (inBlip)
	{
		const vTheta = inBlip.bearingDeg * Math.PI / 180
		const vX = vCenter + Math.sin(vTheta) * vRadius * inBlip.frac
		const vY = vCenter - Math.cos(vTheta) * vRadius * inBlip.frac
		const vSinceSweepFrac = ((((inSweepDeg - inBlip.bearingDeg) % 360) + 360) % 360) / 360
		const vAlpha = Math.max(0.3, 1 - 0.7 * vSinceSweepFrac)
		inCtx.beginPath()
		inCtx.arc(vX, vY, kBlipRadiusPx, 0, Math.PI * 2)
		inCtx.fillStyle = `rgba(217, 122, 58, ${vAlpha.toFixed(3)})`
		inCtx.fill()
	}

	// Point central — en nearMode il gonfle et bat au rythme du ping
	// (4·(1 + pulse·0.6) Android), seul retour directionnel restant.
	const vCenterR = inNearMode ? 4 * (1 + inPulse * 0.6) : 2
	inCtx.beginPath()
	inCtx.arc(vCenter, vCenter, vCenterR, 0, Math.PI * 2)
	inCtx.fillStyle = vColor
	inCtx.fill()

	inCtx.save()
	inCtx.translate(vCenter, vCenter)
	inCtx.rotate((inSweepDeg - 90) * Math.PI / 180)  // 0° = midi (12h), ligne sur l'axe X+

	// Comète : pie-slice plein de 70° côté anti-horaire de la ligne
	// (= derrière, puisque la rotation est horaire), alpha 0.16 uniforme.
	inCtx.beginPath()
	inCtx.moveTo(0, 0)
	inCtx.arc(0, 0, vRadius, -kCometArcDeg * Math.PI / 180, 0)
	inCtx.closePath()
	inCtx.fillStyle = 'rgba(217, 122, 58, 0.16)'
	inCtx.fill()

	// Flash du trait : front-loaded (pulse²) → snap épais puis ré-affine.
	const vFlash = inPulse * inPulse
	const vLineWidth = kBaseLineWidth * (1 + vFlash * 2.5)
	if (vFlash > 0.01)
	{
		inCtx.beginPath()
		inCtx.moveTo(0, 0)
		inCtx.lineTo(vRadius, 0)
		inCtx.strokeStyle = `rgba(217, 122, 58, ${(vFlash * 0.35).toFixed(3)})`
		inCtx.lineWidth = vLineWidth * 2.2
		inCtx.stroke()
	}

	// Trait principal — épaisseur amplifiée par le pulse².
	inCtx.beginPath()
	inCtx.moveTo(0, 0)
	inCtx.lineTo(vRadius, 0)
	inCtx.strokeStyle = vColor
	inCtx.lineWidth = vLineWidth
	inCtx.stroke()

	inCtx.restore()
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}
